import { useEffect, useState } from 'react';
import { ContentWrapper } from '@/components/ContentWrapper';
import { PageHeading, PageSubHeading } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { InstaSignFilters } from './InstaSignFilters';
import { InstaSignTable, SignedDocument } from './InstaSignTable';
import { usePagination } from '@/components/shared/TablePagination';
import { useGetSignatures } from '@/services/paymentsQuery';
import { useAPIOrdering } from '@/lib/hooks/useAPIOrdering';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';

export const InstaSignPage = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [searchText, setSearchText] = useState<string>('');

    const { page, resetPage, rowsPerPage, setRowsPerPage } = usePagination();

    const { sortDirection, sortField, handleRequestSort, orderMap } =
        useAPIOrdering('signedAt', 'desc');

    const { data: signaturesData, isFetching } = useGetSignatures(
        rowsPerPage,
        page * rowsPerPage,
        searchText.length >= 3 ? searchText : undefined,
        sortField,
        sortDirection
    );

    useEffect(() => {
        dispatch(setLoader(isFetching));
    }, [isFetching, dispatch]);

    const mappedSignatures: SignedDocument[] =
        signaturesData?.data?.signatures?.map((sig) => ({
            id: sig.signatureId,
            name: sig.employeeName,
            email: sig.employeeEmail,
            signedDate: sig.signedAt,
            documentName: sig.documentName,
            documentUrl: sig.signedFileUrl
        })) || [];

    const handleSearchChange = (search: string) => {
        setSearchText(search);
        resetPage();
    };

    const handleDownloadDocument = (documentId: string) => {
        const document = mappedSignatures.find((doc) => doc.id === documentId);
        if (document?.documentUrl) {
            window.open(document.documentUrl, '_blank');
        }
    };

    const handleResetFilters = () => {
        setSearchText('');
    };

    return (
        <ContentWrapper>
            <>
                <PageHeading>{t('instaSign')}</PageHeading>
                <PageSubHeading>{t('instaSign_subHeading')}</PageSubHeading>
                <InstaSignFilters
                    onSearchChange={handleSearchChange}
                    onResetFilters={handleResetFilters}
                />
                <InstaSignTable
                    documents={mappedSignatures}
                    total={signaturesData?.data?.total || 0}
                    filteredTotal={signaturesData?.data?.filteredTotal || 0}
                    rowsPerPage={rowsPerPage}
                    handleChangeRowsPerPage={(val) => {
                        setRowsPerPage(val);
                    }}
                    onDownloadDocument={handleDownloadDocument}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onRequestSort={handleRequestSort}
                    orderMap={orderMap}
                />
            </>
        </ContentWrapper>
    );
};
